import { z } from "zod";
import { MAXIMUM_REPORT_FILTER_URL_LENGTH, canonicalUuidSchema, encodedLength, encodedSearch, } from "./report-filters.js";
export const MAXIMUM_VIDEO_SELECTIONS = 4;
export const MINIMUM_VIDEO_COMPARISON_SELECTIONS = 2;
export const MAXIMUM_VIDEO_SELECTION_URL_VALUE_BYTES = 4_096;
export const videoBrandIdSchema = canonicalUuidSchema;
export const videoIdSchema = z
    .string()
    .min(1)
    .max(255)
    .refine((value) => value.trim() === value, {
    message: "Video identifiers must not have surrounding whitespace",
})
    .regex(/^[^\u0000-\u001f\u007f]+$/u, {
    message: "Video identifiers must not contain control characters",
});
export const videoSelectionSchema = z
    .object({
    brandId: videoBrandIdSchema,
    videoId: videoIdSchema,
})
    .strict()
    .readonly();
function rejectDuplicateSelections(values, context) {
    const keys = values.map(({ brandId, videoId }) => `${brandId}\0${videoId}`);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: "custom",
            message: "Video selections must be unique by brand and video",
        });
    }
}
export const videoSelectionUrlStateSchema = z
    .array(videoSelectionSchema)
    .max(MAXIMUM_VIDEO_SELECTIONS)
    .superRefine(rejectDuplicateSelections)
    .readonly();
const videoComparisonSelectionsSchema = z
    .array(videoSelectionSchema)
    .min(MINIMUM_VIDEO_COMPARISON_SELECTIONS)
    .max(MAXIMUM_VIDEO_SELECTIONS)
    .superRefine(rejectDuplicateSelections)
    .readonly();
export const videoComparisonRequestSchema = z
    .object({ videos: videoComparisonSelectionsSchema })
    .strict()
    .readonly();
function jsonQueryValue(schema) {
    return z
        .string()
        .refine((value) => encodedLength(value) <= MAXIMUM_VIDEO_SELECTION_URL_VALUE_BYTES, "Video selection value exceeds the length limit")
        .transform((value, context) => {
        try {
            return JSON.parse(value);
        }
        catch {
            context.addIssue({
                code: "custom",
                message: "Invalid video selection JSON",
            });
            return z.NEVER;
        }
    })
        .pipe(schema);
}
/** Parses the decoded `compare` query value used by shareable UI state. */
export const videoSelectionUrlValueSchema = jsonQueryValue(videoSelectionUrlStateSchema);
/** Parses the decoded `video` query value used by focused-video UI state. */
export const videoIdentityUrlValueSchema = jsonQueryValue(videoSelectionSchema);
/** Parses the decoded `videos` query value accepted by the comparison API. */
export const videoComparisonQueryValueSchema = jsonQueryValue(videoComparisonSelectionsSchema);
export class VideoSelectionUrlError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "VideoSelectionUrlError";
    }
}
function invalidSelectionUrl(code, message) {
    return Object.freeze({ code, kind: "invalid", message });
}
function isBracketedComparisonKey(key) {
    return key.startsWith("compare[");
}
/**
 * Reads only the UI-owned `compare=<JSON>` key. The API-owned `videos` key and
 * all report-filter/view-state keys are deliberately ignored.
 */
export function parseVideoSelectionUrl(input) {
    if (typeof input !== "string") {
        return invalidSelectionUrl("AMBIGUOUS_SELECTION", "Video selections require the raw encoded query string");
    }
    const encoded = encodedSearch(input);
    if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
        return invalidSelectionUrl("URL_TOO_LONG", "Video selection URL exceeds the length limit");
    }
    const params = new URLSearchParams(encoded);
    if ([...params.keys()].some(isBracketedComparisonKey) ||
        params.getAll("compare").length > 1) {
        return invalidSelectionUrl("AMBIGUOUS_SELECTION", "Video selections must use one flat compare value");
    }
    const value = params.get("compare");
    if (value === null)
        return Object.freeze({ kind: "absent" });
    if (encodedLength(value) > MAXIMUM_VIDEO_SELECTION_URL_VALUE_BYTES) {
        return invalidSelectionUrl("SELECTION_TOO_LONG", "Video selection value exceeds the length limit");
    }
    const parsed = videoSelectionUrlValueSchema.safeParse(value);
    if (!parsed.success) {
        return invalidSelectionUrl("INVALID_SELECTION", parsed.error.issues[0]?.message ?? "Invalid video selection");
    }
    return Object.freeze({ kind: "valid", selections: parsed.data });
}
/** Writes only `compare`, preserving the filter URL and the API `videos` key. */
export function serializeVideoSelectionUrl(selections, existing = "") {
    const parsed = videoSelectionUrlStateSchema.safeParse(selections);
    if (!parsed.success) {
        throw new VideoSelectionUrlError("INVALID_SELECTION", parsed.error.issues[0]?.message ?? "Invalid video selection");
    }
    const params = new URLSearchParams(encodedSearch(existing));
    for (const key of [...params.keys()]) {
        if (key === "compare" || isBracketedComparisonKey(key))
            params.delete(key);
    }
    if (parsed.data.length > 0) {
        const value = JSON.stringify(parsed.data);
        if (encodedLength(value) > MAXIMUM_VIDEO_SELECTION_URL_VALUE_BYTES) {
            throw new VideoSelectionUrlError("SELECTION_TOO_LONG", "Video selection value exceeds the length limit");
        }
        params.set("compare", value);
    }
    const encoded = params.toString();
    if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
        throw new VideoSelectionUrlError("URL_TOO_LONG", "Video selection URL exceeds the length limit");
    }
    return encoded;
}
const exactGoogleStorageSignedUrlSchema = z
    .string()
    .min(1)
    .max(16_384)
    .refine((value) => {
    try {
        const url = new URL(value);
        const requiredParameters = [
            "X-Goog-Credential",
            "X-Goog-Date",
            "X-Goog-Expires",
            "X-Goog-SignedHeaders",
            "X-Goog-Signature",
        ];
        return (value.startsWith("https://storage.googleapis.com/") &&
            url.protocol === "https:" &&
            url.hostname === "storage.googleapis.com" &&
            url.port === "" &&
            url.username === "" &&
            url.password === "" &&
            url.pathname.length > 1 &&
            url.hash === "" &&
            url.searchParams.getAll("X-Goog-Algorithm").length === 1 &&
            url.searchParams.get("X-Goog-Algorithm") === "GOOG4-RSA-SHA256" &&
            requiredParameters.every((key) => {
                const values = url.searchParams.getAll(key);
                return values.length === 1 && values[0].length > 0;
            }));
    }
    catch {
        return false;
    }
}, "Expected an exact HTTPS storage.googleapis.com signed URL");
const playbackStatusResponse = (status) => z.object({ status: z.literal(status) }).strict().readonly();
export const videoPlaybackResponseSchema = z.discriminatedUnion("status", [
    playbackStatusResponse("not_tracked"),
    playbackStatusResponse("pending"),
    playbackStatusResponse("unavailable"),
    z
        .object({
        expiresAt: z.iso.datetime({ offset: true }),
        signedUrl: exactGoogleStorageSignedUrlSchema,
        status: z.literal("hosted"),
    })
        .strict()
        .readonly(),
]);
export const VIDEO_PLAYBACK_FORBIDDEN_RESPONSE = Object.freeze({
    code: "VIDEO_PLAYBACK_FORBIDDEN",
    message: "Video playback is not available",
    statusCode: 403,
});
export const VIDEO_PLAYBACK_REQUEST_INVALID_RESPONSE = Object.freeze({
    code: "VIDEO_PLAYBACK_REQUEST_INVALID",
    message: "Invalid video playback request",
    statusCode: 400,
});
export const VIDEO_PLAYBACK_UNAVAILABLE_RESPONSE = Object.freeze({
    code: "VIDEO_PLAYBACK_UNAVAILABLE",
    message: "Video playback is temporarily unavailable",
    statusCode: 503,
});
export const videoPlaybackErrorResponseSchema = z.discriminatedUnion("code", [
    z
        .object({
        code: z.literal(VIDEO_PLAYBACK_FORBIDDEN_RESPONSE.code),
        message: z.literal(VIDEO_PLAYBACK_FORBIDDEN_RESPONSE.message),
        statusCode: z.literal(VIDEO_PLAYBACK_FORBIDDEN_RESPONSE.statusCode),
    })
        .strict()
        .readonly(),
    z
        .object({
        code: z.literal(VIDEO_PLAYBACK_REQUEST_INVALID_RESPONSE.code),
        message: z.literal(VIDEO_PLAYBACK_REQUEST_INVALID_RESPONSE.message),
        statusCode: z.literal(VIDEO_PLAYBACK_REQUEST_INVALID_RESPONSE.statusCode),
    })
        .strict()
        .readonly(),
    z
        .object({
        code: z.literal(VIDEO_PLAYBACK_UNAVAILABLE_RESPONSE.code),
        message: z.literal(VIDEO_PLAYBACK_UNAVAILABLE_RESPONSE.message),
        statusCode: z.literal(VIDEO_PLAYBACK_UNAVAILABLE_RESPONSE.statusCode),
    })
        .strict()
        .readonly(),
]);
//# sourceMappingURL=video.js.map